import React, { useCallback, useMemo, type FC } from "react";
import { Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useGetSeries } from "../../../../queries/dynamicBonuses/useGetSeries";
import { useUpdateSeries } from "../../../../queries/dynamicBonuses/useUpdateSeries";
import { toSeriesIn } from "../../../../queries/dynamicBonuses/toWriteBody";
import CreateCampaignWizard from "../Campaigns/CreateCampaignWizard/CreateCampaignWizard";
import { seriesToWizardValues } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import { useCampaignEditLocks } from "../useCampaignEditLocks";
import type { CampaignIn, SeriesIn } from "../types";
import styles from "../stylesDynamicBonuses.module.css";

/**
 * Series editing reuses the create campaign wizard — hydrate via
 * `seriesToWizardValues`, assemble `SeriesIn` on save, and PUT via
 * `toSeriesIn`. Does not pass `editCampaign` (campaign transitions / Send
 * Test are wrong for series; Activate/Pause stay on the detail page).
 *
 * BONENG-996 — Braze (series) edits follow the same lock rules as PAM
 * campaigns: archived is fully read-only; active/paused/expired keep bonus
 * rules locked and allow banner/display updates only. Drafts are fully
 * editable.
 */
const EditSeriesPage: FC = () => {
  const navigate = useNavigate();
  const { seriesId } = useParams<{ seriesId: string }>();
  const id = seriesId ?? "";
  const { data: series, isLoading, isError } = useGetSeries(id);
  const { mutate: updateSeries, isPending } = useUpdateSeries(id);
  const { isBannerOnlyEdit, isFullyReadOnly } = useCampaignEditLocks(
    series?.status,
  );

  const initialValues = useMemo(
    () => (series ? seriesToWizardValues(series) : undefined),
    [series],
  );

  const handleSubmit = useCallback(
    (payload: CampaignIn | SeriesIn) => {
      if (!series) return;
      // Series edit only receives SeriesIn from the wizard series branch.
      const seriesPayload = payload as SeriesIn;
      const writableSeries = toSeriesIn(series);
      const updatePayload = isBannerOnlyEdit
        ? {
            ...writableSeries,
            steps: writableSeries.steps.map((step, index) => ({
              ...step,
              banner: seriesPayload.steps[index]?.banner ?? step.banner,
              display_locations:
                seriesPayload.steps[index]?.display_locations ??
                step.display_locations,
            })),
          }
        : toSeriesIn(series, seriesPayload);
      updateSeries(updatePayload, {
        onSuccess: () =>
          navigate(
            NAVIGATION_ROUTES.DYNAMIC_BONUS_SERIES_DETAIL.replace(
              ":seriesId",
              series.series_campaign_id,
            ),
          ),
      });
    },
    [series, isBannerOnlyEdit, updateSeries, navigate],
  );

  if (isLoading || isError || !series) {
    return (
      <>
        <FeatureFlagGuard feature="dynamicBonusesExternal" mode="route">
          {isLoading ? (
            <Spin data-testid="edit-series-loading" />
          ) : (
            <p className={styles.listError}>
              {AppStrings.PAM_CAMPAIGNS_SERIES_LOAD_ERROR}
            </p>
          )}
        </FeatureFlagGuard>
      </>
    );
  }

  const isReadOnly = isFullyReadOnly;
  const rulesLocked = !isReadOnly && isBannerOnlyEdit;

  return (
    <FeatureFlagGuard feature="dynamicBonusesExternal" mode="route">
      <CreateCampaignWizard
        title={AppStrings.PAM_DYNAMIC_BONUS_SERIES_EDIT_TITLE}
        submitLabel={
          isReadOnly ? undefined : AppStrings.PAM_DYNAMIC_BONUS_SERIES_SAVE
        }
        initialValues={initialValues}
        onSubmit={isReadOnly ? undefined : handleSubmit}
        isSubmitting={isReadOnly ? false : isPending}
        readOnly={isReadOnly}
        rulesLocked={rulesLocked}
        lockBrazeSource
      />
    </FeatureFlagGuard>
  );
};

export default EditSeriesPage;
